import { Alert, Box, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DocumentUploadForm } from "@/components/documents/DocumentUploadForm";
import type { DocumentEntityType, DocumentType } from "@/api/types";

const VALID_ENTITY_TYPES: DocumentEntityType[] = [
  "organization",
  "site",
  "location",
  "location_artifact",
  "study",
  "disease_area",
  "participation",
  "participation_task",
  "investigator",
];

/** Standalone page wrapper around DocumentUploadForm. Most upload entry
 * points now use DocumentUploadDrawer to stay in context; this page
 * remains for direct-link uploads (the DocumentsSection card on entity
 * detail pages and any deep-linked URLs). */
export default function DocumentUpload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const entityType = searchParams.get("entity_type") as DocumentEntityType | null;
  const entityId = searchParams.get("entity_id");
  const initialDocType =
    (searchParams.get("document_type") as DocumentType | null) ?? "other";
  const backTo = searchParams.get("back_to");

  const isValidContext =
    entityType !== null &&
    entityId !== null &&
    VALID_ENTITY_TYPES.includes(entityType);

  if (!isValidContext) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <Alert severity="error">
          Cannot upload without an entity context. Open the document repository
          from a Site, Organization, or other entity detail page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Upload document
      </Typography>
      <DocumentUploadForm
        entityType={entityType}
        entityId={entityId}
        initialDocType={initialDocType}
        onComplete={(documentId) =>
          navigate(backTo ?? `/clinops/documents/${documentId}`)
        }
        onCancel={() => navigate(-1)}
      />
    </Box>
  );
}
